/** @jsx jsx */
import { css, jsx } from '@emotion/core'
import { Heading, HFlow, Icon, Modal, ModalBody, ModalFooter, Tooltip, VFlow } from 'bold-ui'
import { FooterButton } from 'components/footer-button'
import { Form, FormProps } from 'components/form/final-form'
import { ButtonLink } from 'components/route'
import { FormApi } from 'final-form'
import arrayMutators from 'final-form-arrays'
import { IdentidadeGeneroEnum, SexoEnum } from 'graphql/types.generated'
import { pickBy, transform } from 'lodash'
import { Fragment, useMemo } from 'react'
import { Route, Switch, useHistory, useRouteMatch } from 'react-router'
import { metaPath } from 'util/metaPath'
import { isObjectDeepEmpty } from 'util/object'

import {
  ResultadoExameQueryModel,
  ResultadosExamesModel,
  ResultadosExamesSolicitadosModel,
  SolicitacaoExameQueryModel,
} from '../model'
import { isExameEspecifico, removePrefix } from '../util'
import { SolicitacaoExameField } from './exames-com-solicitacao/SolicitacaoExameField'
import { ExamesSemSolicitacaoField } from './exames-sem-solicitacao/ExamesSemSolicitacaoField'
import { adicionarResultadosExamesValidator } from './validator'

interface AdicionarResultadosExamesModalFormProps
  extends Pick<FormProps<ResultadosExamesModel>, 'initialValues' | 'onSubmit'> {
  editing: boolean
  solicitacoes: SolicitacaoExameQueryModel[]
  resultadosSolicitados: Record<ID, ResultadoExameQueryModel>
  cidadaoSexo: SexoEnum
  cidadaoIdentidadeGeneroDbEnum: IdentidadeGeneroEnum
  cidadaoDataNascimento: LocalDate
  numberOfResultadosNaoAvaliados: number
}

const path = metaPath<ResultadosExamesModel>()

export const AdicionarResultadosExamesModalForm = (props: AdicionarResultadosExamesModalFormProps) => {
  const {
    editing,
    solicitacoes,
    resultadosSolicitados,
    cidadaoSexo,
    cidadaoIdentidadeGeneroDbEnum,
    cidadaoDataNascimento,
    initialValues,
    numberOfResultadosNaoAvaliados,
    onSubmit,
  } = props

  const history = useHistory()
  const match = useRouteMatch()

  const handleSubmit = (values: ResultadosExamesModel, form: FormApi<ResultadosExamesModel>) => {
    onSubmit(
      {
        resultadosSemSolicitacao: values.resultadosSemSolicitacao,
        resultadosComSolicitacao: formatValues(values.resultadosComSolicitacao, resultadosSolicitados),
      },
      form
    )
    handleCloseModal(form.reset)
  }

  const handleCloseModal = (reset: (values?: object) => void) => {
    history.push(match.url.replace('/resultados-exames/adicionar', ''))
    setTimeout(() => reset())
  }

  const pluralText: boolean = numberOfResultadosNaoAvaliados > 1

  return (
    <Fragment>
      <Tooltip
        text={
          numberOfResultadosNaoAvaliados > 0 &&
          `${pluralText ? 'Existem' : 'Existe'} ${numberOfResultadosNaoAvaliados} ${
            pluralText ? 'exames' : 'exame'
          } sem resultado informado`
        }
      >
        <ButtonLink to={`${match.url}/resultados-exames/adicionar`} type='button' kind='primary' size='small'>
          <Icon
            icon={editing ? 'penOutline' : 'plus'}
            style={css`
              margin-right: 0.5rem;
            `}
          />
          {`${editing ? 'Editar' : 'Adicionar'} resultados de exames${
            numberOfResultadosNaoAvaliados > 0 ? ` (${numberOfResultadosNaoAvaliados})` : ''
          }`}
        </ButtonLink>
      </Tooltip>

      <Switch>
        <Route path={`${match.url}/resultados-exames/adicionar`}>
          <Form<ResultadosExamesModel>
            onSubmit={handleSubmit}
            initialValues={initialValues}
            validate={useMemo(() => adicionarResultadosExamesValidator(cidadaoDataNascimento, resultadosSolicitados), [
              cidadaoDataNascimento,
              resultadosSolicitados,
            ])}
            mutators={{ ...arrayMutators }}
            render={(formProps) => (
              <Modal open onClose={() => handleCloseModal(formProps.form.reset)}>
                <ModalBody>
                  <VFlow vSpacing={1.5}>
                    <Heading level={1}>{editing ? 'Editar' : 'Adicionar'} resultados de exames</Heading>

                    <ExamesSemSolicitacaoField
                      name={path.resultadosSemSolicitacao}
                      cidadaoSexo={cidadaoSexo}
                      cidadaoIdentidadeGeneroDbEnum={cidadaoIdentidadeGeneroDbEnum}
                      cidadaoDataNascimento={cidadaoDataNascimento}
                    />

                    {solicitacoes?.length > 0 && (
                      <VFlow>
                        {solicitacoes?.map((solicitacao) => (
                          <SolicitacaoExameField
                            key={solicitacao.id}
                            name={path.resultadosComSolicitacao}
                            solicitacao={solicitacao}
                          />
                        ))}
                      </VFlow>
                    )}
                  </VFlow>
                </ModalBody>

                <ModalFooter>
                  <HFlow justifyContent='flex-end'>
                    <FooterButton kind='normal' onClick={() => handleCloseModal(formProps.form.reset)}>
                      Cancelar
                    </FooterButton>
                    <FooterButton kind='primary' onClick={formProps.handleSubmit}>
                      Salvar
                    </FooterButton>
                  </HFlow>
                </ModalFooter>
              </Modal>
            )}
          />
        </Route>
      </Switch>
    </Fragment>
  )
}

const formatValues = (
  values: ResultadosExamesSolicitadosModel,
  resultadosSolicitados: Record<ID, ResultadoExameQueryModel>
) => {
  const filteredValues = pickBy(values, (value, key) => {
    const isEspecifico = isExameEspecifico(resultadosSolicitados[removePrefix(key)].exame)

    return !!(isEspecifico ? !isObjectDeepEmpty(value.resultado) : trimRemoveBreakLines(value.resultado as string))
  })

  return transform(
    filteredValues,
    (result, value, key) =>
      (result[key] = {
        ...value,
        resultado: isExameEspecifico(resultadosSolicitados[removePrefix(key)].exame)
          ? value.resultado
          : (value.resultado as string).trim(),
      }),
    {}
  )
}

const trimRemoveBreakLines = (value: string) => value?.replaceAll('\n', '').trim()
